import { Link } from 'react-router-dom';
import { MapPin, Phone, Heart, Truck } from 'lucide-react';

const LOGO_PNG = "/logo.png";

// Instagram colored icon
const InstagramColored = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="ig-footer" x1="0%" y1="100%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#FFDC80" />
        <stop offset="25%" stopColor="#FCAF45" />
        <stop offset="50%" stopColor="#F77737" />
        <stop offset="75%" stopColor="#F56040" />
        <stop offset="100%" stopColor="#C13584" />
      </linearGradient>
    </defs>
    <rect x="2" y="2" width="20" height="20" rx="5" stroke="url(#ig-footer)" strokeWidth="2" fill="none" />
    <circle cx="12" cy="12" r="4" stroke="url(#ig-footer)" strokeWidth="2" fill="none" />
    <circle cx="17.5" cy="6.5" r="1.5" fill="url(#ig-footer)" />
  </svg>
);

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-900 text-white" data-testid="footer">
      {/* Accent Top Bar */}
      <div className="h-1 bg-gradient-to-r from-cyan-500 via-blue-600 to-violet-600"></div>

      {/* Main Footer */}
      <div className="container-custom py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="mb-5 flex items-center gap-3">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #fffbf0 0%, #fef3e2 50%, #fde9cc 100%)' }}>
                <img
                  src={LOGO_PNG}
                  alt="Nate's Pharmacy Logo"
                  className="h-8 w-8 object-contain"
                />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Nate's Pharmacy</h3>
                <p className="text-xs text-cyan-400">More Than Medicine, We Care</p>
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-5">
              Your neighborhood pharmacy since 2007. Serving Red Hook and all of Brooklyn with care.
            </p>
            <a
              href="https://www.instagram.com/nates.pharmacy"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600/20 via-pink-600/20 to-orange-500/20 hover:from-purple-600/40 hover:via-pink-600/40 hover:to-orange-500/40 px-4 py-2.5 rounded-full transition-all duration-300 border border-pink-500/30 hover:border-pink-500/50 hover:scale-105"
              data-testid="footer-instagram"
            >
              <InstagramColored className="w-5 h-5" />
              <span className="text-sm text-pink-300">@nates.pharmacy</span>
            </a>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-sm uppercase tracking-wider text-slate-400 mb-4">Quick Links</h3>
            <nav className="flex flex-col gap-2">
              <Link to="/our-story" className="text-slate-300 hover:text-cyan-400 text-sm transition-colors duration-200 hover:translate-x-1 inline-block" data-testid="footer-our-story">Our Story</Link>
              <Link to="/services" className="text-slate-300 hover:text-cyan-400 text-sm transition-colors duration-200 hover:translate-x-1 inline-block" data-testid="footer-services">Services</Link>
              <Link to="/transfer" className="text-slate-300 hover:text-cyan-400 text-sm transition-colors duration-200 hover:translate-x-1 inline-block" data-testid="footer-transfer">Transfer Prescription</Link>
              <Link to="/refill" className="text-slate-300 hover:text-cyan-400 text-sm transition-colors duration-200 hover:translate-x-1 inline-block" data-testid="footer-refill">Refill Prescription</Link>
              <Link to="/delivery" className="text-slate-300 hover:text-cyan-400 text-sm transition-colors duration-200 hover:translate-x-1 inline-block" data-testid="footer-delivery">Request Delivery</Link>
            </nav>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-semibold text-sm uppercase tracking-wider text-slate-400 mb-4">Contact</h3>
            <div className="space-y-3">
              <a
                href="https://maps.google.com/?q=376+Van+Brunt+St,+Brooklyn,+NY+11231"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-3 text-slate-300 hover:text-white transition-colors"
                data-testid="footer-address"
              >
                <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5 text-cyan-400" />
                <span className="text-sm">376 Van Brunt St<br />Brooklyn, NY 11231</span>
              </a>
              <a
                href="tel:+17187970200"
                className="flex items-center gap-3 text-slate-300 hover:text-white transition-colors"
                data-testid="footer-phone"
              >
                <Phone className="w-4 h-4 flex-shrink-0 text-green-400" />
                <span className="text-sm font-medium">(718) 797-0200</span>
              </a>
            </div>
          </div>

          {/* Hours */}
          <div>
            <h3 className="font-semibold text-sm uppercase tracking-wider text-slate-400 mb-4">Hours</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-slate-300">
                <span>Mon - Fri</span>
                <span className="text-white font-medium">9AM - 7PM</span>
              </div>
              <div className="flex justify-between text-slate-300">
                <span>Saturday</span>
                <span className="text-white font-medium">9AM - 3PM</span>
              </div>
              <div className="flex justify-between text-slate-300">
                <span>Sunday</span>
                <span className="text-slate-500">Closed</span>
              </div>
            </div>
          </div>
        </div>

        {/* Free Delivery Banner */}
        <div className="mt-10 p-5 rounded-2xl bg-gradient-to-r from-cyan-600/20 via-blue-600/20 to-violet-600/20 border border-cyan-500/20">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                <Truck className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <h4 className="font-semibold text-white">FREE Delivery Across NYC</h4>
                <p className="text-slate-400 text-sm">All 5 boroughs • Priority Red Hook service</p>
              </div>
            </div>
            <Link
              to="/delivery"
              className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:from-cyan-400 hover:to-blue-400 px-6 py-2.5 rounded-full font-medium text-sm whitespace-nowrap transition-all duration-300 hover:scale-105 shadow-lg shadow-cyan-500/30"
              data-testid="footer-delivery-cta"
            >
              Request Delivery
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="container-custom py-5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-slate-500">
            <p>© {currentYear} Nate's Pharmacy. All rights reserved.</p>
            <p className="flex items-center gap-1">
              Made with <Heart className="w-3 h-3 text-red-500 fill-red-500" /> in Brooklyn, NY
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
